import { ProductCard } from "@/components/marketplace/product/product-card"
import type { Product } from "@/components/marketplace/types"
import { ArrowIcon, Heading, LinkButton, Stack, Text, WindowedReel } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

interface BrandSpotlightBandProps {
  eyebrow: string
  title: string
  sub?: string
  cta?: string
  ctaHref?: string
  image: string
  imageCrop?: ImageCrop
  imageAlt?: string
  imageTall?: string
  tiles: Product[]
}

export const BrandSpotlightBand = ({ eyebrow, title, sub, cta, ctaHref, image, imageCrop, imageAlt, imageTall, tiles }: BrandSpotlightBandProps) => (
  <div className="@container">
    <Stack gap="350" className="@3xl:gap-500">
      <div className="press-banner rounded-20x relative flex min-h-85 w-full items-end overflow-hidden @3xl:min-h-130 @3xl:items-center">
        {imageTall && <TileImage src={imageTall} alt={imageAlt ?? ""} fill sizes="100vw" className="object-cover @3xl:hidden" />}
        <TileImage src={image} crop={imageCrop} alt={imageAlt ?? ""} fill sizes="100vw" className={`object-cover ${imageTall ? "hidden @3xl:block" : ""}`} />
        <span className="bg-wash-corner @3xl:bg-wash-inset absolute inset-0" aria-hidden />
        <CoverLink href={cta ? ctaHref : undefined} />
        <Stack align="start" gap="400" className="z-pill pointer-events-none relative max-w-85 p-700 @3xl:max-w-155 @3xl:p-1200">
          <Text
            as="span"
            variant="label"
            family="display"
            tone="inverted"
            className="motion-safe:animate-rise-in bg-fg-strong inline-flex h-7.5 items-center rounded-full px-300 font-bold"
          >
            {eyebrow}
          </Text>
          <Heading as="h2" variant="displaySm" family="display" tone="strong" balance className="motion-safe:animate-rise-in animation-delay-100 @3xl:text-display-2xl">
            {title}
          </Heading>
          {sub && (
            <Text as="p" variant="bodyLg" family="text" tone="default" className="motion-safe:animate-rise-in animation-delay-200 @3xl:text-lede">
              {sub}
            </Text>
          )}
          {cta && (
            <LinkButton
              href={ctaHref}
              variant="ink"
              size="md"
              className="lift motion-safe:animate-rise-in animation-delay-300 pointer-events-auto mt-100 min-w-0 font-bold"
              iconRight={<ArrowIcon size={16} />}
            >
              {cta}
            </LinkButton>
          )}
        </Stack>
      </div>
      <WindowedReel windowSize={4} gap="250" snap="proximity" inset className="mx-gut-bleed pb-100 @3xl:gap-400">
        {tiles.map((tile) => (
          <div key={tile.id} className="w-42 @3xl:w-53">
            <ProductCard product={tile} size="compact" />
          </div>
        ))}
      </WindowedReel>
    </Stack>
  </div>
)
